import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Scan, ArrowLeft, Mail, MessageSquare, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !message) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&message=${encodeURIComponent(message)}`);
      toast.success('Message sent! We\'ll get back to you soon.');
      setName('');
      setEmail('');
      setMessage('');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <nav className="backdrop-blur-md bg-white/70 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Scan className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Shelfie</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            data-testid="back-home-button"
            className="text-gray-700 hover:text-emerald-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Contact Form */}
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="glass rounded-2xl p-8 sm:p-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Get in Touch</h1>
            <p className="text-gray-600">Have questions or feedback? We'd love to hear from you!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-emerald-600" />
                Name
              </Label>
              <Input
                id="name"
                data-testid="contact-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="border-gray-300 focus:border-emerald-500"
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-emerald-600" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="border-gray-300 focus:border-emerald-500"
              />
            </div>

            <div>
              <Label htmlFor="message" className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
                Message
              </Label>
              <Textarea
                id="message"
                data-testid="contact-message-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you're thinking..."
                rows={6}
                className="border-gray-300 focus:border-emerald-500 resize-none"
              />
            </div>

            <Button
              type="submit"
              data-testid="submit-contact-button"
              disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600 mb-2">Or reach us directly at:</p>
            <a
              href="mailto:anniemiller71@gmail.com"
              className="text-emerald-600 font-semibold hover:text-emerald-700"
            >
              anniemiller71@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
